import Button from "antd/es/button"
import Form from "antd/es/form"
import List from "antd/es/list"
import Upload from "antd/es/upload"
import { ArrowRightIcon, CloudUploadIcon, TrashIcon } from "lucide-react"
import { useCallback, useState } from "react"

import { EMPTY_ARRAY } from "../constants"
import type { FileData } from "../documents/types"
import useErrorPopup from "../hooks/useErrorPopup"
import {
  getMimeTypeFromFilename,
  MimeType,
  QUESTIONNAIRE_SUPPORTED_MIMETYPES,
  SOURCE_DOCUMENT_SUPPORTED_MIMETYPES,
} from "../types/mimetype"
import { QuestionnaireWorkflowWizardFromFiles } from "./QuestionnaireWorkflowWizard"
import { indexFileDocument } from "./QuestionnaireWorkflowWizard/indexDocument"
import SelectTags from "./SelectTags"

const { Dragger } = Upload

interface Props {
  onClose: () => void
  isCompletedQuestionnaireActions: boolean
}

const UploadUserDocumentForm: React.FC<Props> = ({
  onClose,
  isCompletedQuestionnaireActions,
}) => {
  const [uploading, setUploading] = useState<boolean>(false)
  const [files, setFiles] = useState<File[]>([])
  const [completedQuestionnaireFiles, setCompletedQuestionnaireFiles] =
    useState<FileData[]>([])
  const { messageApi, handleError, handleSuccess } = useErrorPopup()
  const [tags, setTags] = useState<string[]>([])

  const buttonSubText = isCompletedQuestionnaireActions ? (
    <>
      Upload a completed questionnaire and we will import the answers.
      <br />
      Only Excel files and Sheets are currently supported. Other documents
      should be uploaded as Source Documents.
    </>
  ) : (
    <>
      Upload a document and we will parse it to answer questions.
      <br />
      Spreadsheets will be interpreted as completed questionnaires.
    </>
  )

  const submitFiles = useCallback(
    async (files: File[]) => {
      void messageApi.open({
        type: "info",
        content: "Uploading files...",
        key: "uploading-files",
        duration: 10,
      })
      try {
        setUploading(true)
        await Promise.allSettled(
          files.map(async (file) => {
            try {
              const fileContents = await file.arrayBuffer()
              const mimeType = file.type as MimeType

              // Do not index XLSX files as these will be handled with the
              // import wizard
              // TODO(mgraczyk): Check isCompletedQuestionnaireActions here once
              // the UI indicates what is and is not a completed questionnaire.
              if (QUESTIONNAIRE_SUPPORTED_MIMETYPES.includes(mimeType)) {
                setCompletedQuestionnaireFiles((prevFiles) => [
                  ...prevFiles,
                  {
                    fileContents,
                    fileType: mimeType,
                    fileName: file.name,
                  },
                ])
                return
              }

              // TODO(mgraczyk): Show upload progress.
              await indexFileDocument(
                {
                  kind: "NON_QUESTIONNAIRE_FILE",
                  fileContents,
                  mimeType,
                  fileType: mimeType,
                  fileName: file.name,
                },
                tags,
              )
            } catch (error) {
              handleError({
                error,
                prefix: `Error uploading file: ${file.name}`,
              })
            }
          }),
        )
        handleSuccess("Files uploaded successfully")
      } catch (error) {
        handleError({ error, prefix: "Can't upload files" })
      } finally {
        setFiles([])
        setUploading(false)
        messageApi.destroy("uploading-files")
      }
    },
    [messageApi, handleSuccess, handleError, tags],
  )

  const handleFileChange = useCallback(
    (file: File): boolean => {
      if (file === undefined) {
        handleError({ message: "No file selected" })
        return false
      }
      let typedFile = file
      if (file.type === "") {
        const mimeType = getMimeTypeFromFilename(file.name)
        if (mimeType !== MimeType.UNKNOWN) {
          typedFile = new File([file], file.name, { type: mimeType })
        }
      }
      const fileType = typedFile.type as MimeType

      const supportedMimeTypes = isCompletedQuestionnaireActions
        ? QUESTIONNAIRE_SUPPORTED_MIMETYPES
        : SOURCE_DOCUMENT_SUPPORTED_MIMETYPES
      if (!supportedMimeTypes.includes(fileType)) {
        console.warn("Invalid file type", typedFile.name, fileType)
        handleError({
          message: "Invalid file type. Please upload a supported file type",
        })
        return false
      }
      setFiles((prevFiles) => [...prevFiles, typedFile])
      return false
    },
    [handleError, isCompletedQuestionnaireActions],
  )

  const uploadForm = (
    <div className="m-4 flex flex-col gap-4">
      <Dragger
        beforeUpload={handleFileChange}
        showUploadList={false}
        multiple={true}
      >
        <div className="p-2">
          <CloudUploadIcon className="text-primary mx-auto my-4" size="48" />
          <p className="ant-upload-text">
            Click or drag file to this area to upload
          </p>
          <p className="ant-upload-hint">{buttonSubText}</p>
        </div>
      </Dragger>
      {files.length > 0 && (
        <List className="mb-4 mt-2">
          {files.map((file, index) => (
            <List.Item
              className="bg-gray-25 my-1 flex justify-between rounded-md !border-0 !py-[4px] !pl-[12px] !pr-[8px]"
              key={index}
            >
              <span className="text-sm">{file.name}</span>
              <Button
                type="text"
                onClick={() =>
                  setFiles((files) => files.filter((_, idx) => idx !== index))
                }
                icon={<TrashIcon size="1em" />}
              />
            </List.Item>
          ))}
        </List>
      )}
      <Form.Item
        label="Tags (optional)"
        name="tags"
        required={false}
        className="mb-0"
      >
        <SelectTags onChange={setTags} />
      </Form.Item>
      <div className="flex justify-end">
        <Button
          type="primary"
          disabled={files.length === 0 || uploading}
          onClick={async () => {
            await submitFiles(files)
            onClose()
          }}
          icon={<ArrowRightIcon size="1em" />}
          className="font-bold"
        >
          Continue
        </Button>
      </div>
    </div>
  )

  return (
    <>
      {completedQuestionnaireFiles.length > 0 && (
        <QuestionnaireWorkflowWizardFromFiles
          onClose={() => {
            setCompletedQuestionnaireFiles(EMPTY_ARRAY)
          }}
          files={completedQuestionnaireFiles}
          handleDocumentProcessing={(doc) => indexFileDocument(doc, tags)}
          isCompletedQuestionnaire
        />
      )}
      {uploadForm}
    </>
  )
}

export default UploadUserDocumentForm
